<template>
  <modal name="SavedFiltersModal" :width="440" height="auto" :adaptive="true" :scrollable="true" class="saved-filters-vmodal" @before-open="onOpen">
    <div class="saved-filters-modal" aria-labelledby="saved-filters-title">
      <div class="saved-filters-header">
        <strong id="saved-filters-title">Filtros guardados</strong>
        <button type="button" title="Cerrar" @click="$modal.hide('SavedFiltersModal')"><font-awesome-icon icon="times" /></button>
      </div>

      <div class="save-current">
        <input ref="nameInput" v-model="name" type="text" maxlength="60" placeholder="Nombre del filtro" :disabled="!hasCurrentFilter || saving || atLimit" @keyup.enter="saveCurrent">
        <button type="button" class="save-btn" :disabled="!hasCurrentFilter || !name.trim() || saving || atLimit" @click="saveCurrent">
          {{ saving ? "Guardando…" : "Guardar filtro actual" }}
        </button>
      </div>
      <p v-if="!hasCurrentFilter" class="hint">Aplique un filtro (rápido o avanzado) en la capa para poder guardarlo.</p>
      <p v-else-if="atLimit" class="hint warning">Alcanzó el máximo de {{ maxPerLayer }} filtros guardados para esta capa. Elimine alguno para guardar uno nuevo.</p>
      <p v-else class="hint">{{ presets.length }}/{{ maxPerLayer }} filtros guardados.</p>

      <ul v-if="presets.length" class="preset-list">
        <li v-for="preset in presets" :key="preset.id" class="preset-row" :class="{ active: preset.id === activePresetId, selected: preset.id === selectedId }">
          <div v-if="editingId === preset.id" class="preset-edit">
            <input v-model="editName" type="text" maxlength="60" class="rename-input" @keyup.enter="confirmRename(preset)" @keyup.esc="cancelRename">
            <button type="button" class="text-btn" :disabled="busy" @click="confirmRename(preset)">Guardar</button>
            <button type="button" class="text-btn" :disabled="busy" @click="cancelRename">Cancelar</button>
          </div>
          <label v-else class="preset-info">
            <input type="checkbox" :checked="preset.id === selectedId" @click="toggleSelect(preset)">
            <span class="preset-name" :title="preset.name">{{ preset.name }}</span>
            <span v-if="preset.id === activePresetId" class="preset-badge">Cargado</span>
            <span v-else-if="isIncompatible(preset.id)" class="preset-badge warning" :title="incompatibleReason(preset.id)">No compatible</span>
          </label>
        </li>
      </ul>
      <p v-else class="hint">Todavía no hay filtros guardados para esta capa.</p>

      <div v-if="presets.length" class="preset-actions-bar">
        <button type="button" class="text-btn" :disabled="!selectedPreset || busy" @click="load(selectedPreset)">Cargar</button>
        <button type="button" class="text-btn" :disabled="!selectedPreset || !hasCurrentFilter || busy" title="Sobrescribe este filtro con el que está aplicado ahora" @click="overwrite(selectedPreset)">Sobreescribir</button>
        <button type="button" class="text-btn" :disabled="!selectedPreset || busy" @click="startRename(selectedPreset)">Renombrar</button>
        <button type="button" class="text-btn danger" :disabled="!selectedPreset || busy" @click="confirmDelete(selectedPreset)">Eliminar</button>
      </div>
    </div>
  </modal>
</template>

<script>
import { attributesTableService } from "@/services/layers/attributesTable";

// Debe coincidir con SavedFilterMaxPerLayer en
// visor-mapas-backend/models/saved_filter.go — el backend es la fuente de
// verdad (rechaza con 409 al llegar al tope), esto es solo para mostrar el
// aviso antes de intentar guardar.
const MAX_PER_LAYER = 30;
const SAVED_FILTER_LIMIT_MESSAGE = "Alcanzó el máximo de filtros guardados para esta capa";

export default {
  name: "SavedFiltersModal",
  data() {
    return {
      name: "",
      saving: false,
      busy: false,
      editingId: null,
      editName: "",
      hasCurrentFilter: false,
      activePresetId: null,
      selectedId: null,
    };
  },
  computed: {
    layer() { return this.$store.getters["attributeTable/layer"]; },
    presets() { return this.layer ? this.$store.getters["savedFilters/forLayer"](this.layer.id) : []; },
    selectedPreset() { return this.presets.find(preset => preset.id === this.selectedId) || null; },
    maxPerLayer() { return MAX_PER_LAYER; },
    atLimit() { return this.presets.length >= MAX_PER_LAYER; },
  },
  watch: {
    presets() {
      this.refreshLiveState();
      if (this.selectedId && !this.presets.some(preset => preset.id === this.selectedId)) this.selectedId = null;
    },
    layer() { this.resetForm(); this.refreshLiveState(); },
  },
  mounted() {
    window.addEventListener("attribute-filter-state-change", this.onExternalFilterStateChange);
  },
  beforeDestroy() {
    window.removeEventListener("attribute-filter-state-change", this.onExternalFilterStateChange);
  },
  methods: {
    errorMessage(error) {
      return (error.response && error.response.data && error.response.data.message) || "No fue posible comunicarse con el servidor.";
    },
    onExternalFilterStateChange(event) {
      if (this.layer && event.detail && event.detail.layerId === this.layer.id) this.refreshLiveState();
    },
    refreshLiveState() {
      if (!this.layer) { this.hasCurrentFilter = false; this.activePresetId = null; return; }
      this.hasCurrentFilter = !!attributesTableService.captureAppliedFilter(this.layer);
      const match = this.presets.find(preset => attributesTableService.matchesAppliedFilter(this.layer.id, preset));
      this.activePresetId = match ? match.id : null;
    },
    resetForm() {
      this.name = ""; this.saving = false; this.busy = false; this.editingId = null; this.editName = ""; this.selectedId = null;
    },
    toggleSelect(preset) { this.selectedId = this.selectedId === preset.id ? null : preset.id; },
    // Carga perezosa: la carga masiva de VisorViewer.vue solo cubre las capas
    // del catálogo del mapa (ver store/modules/savedFilters.js), así que acá
    // se refresca siempre al abrir — barato (una sola petición) y cubre
    // también capas importadas.
    async onOpen() {
      this.resetForm();
      if (this.layer) await this.$store.dispatch("savedFilters/loadForLayer", this.layer.id);
      this.refreshLiveState();
      this.$nextTick(() => this.$refs.nameInput && this.$refs.nameInput.focus());
    },
    isIncompatible(id) { return this.$store.getters["savedFilters/isIncompatible"](id); },
    incompatibleReason(id) { return this.$store.getters["savedFilters/reason"](id); },
    async saveCurrent() {
      if (!this.hasCurrentFilter || !this.name.trim() || this.saving || !this.layer) return;
      const filter = attributesTableService.captureAppliedFilter(this.layer);
      if (!filter) return;
      const name = this.name.trim();
      this.saving = true;
      try {
        await this.$store.dispatch("savedFilters/create", { layerId: this.layer.id, name, filter });
        this.name = "";
        this.$store.dispatch("toast/show", { message: "Filtro guardado", type: "success" });
      } catch (error) {
        // routes/saved_filter.go::CreateSavedFilter responde 409 tanto para
        // nombre duplicado como para tope de filtros alcanzado — hay que
        // distinguirlos por el mensaje, si no el segundo caso termina
        // mostrando el toast equivocado ("nombre duplicado" sobre un nombre
        // que no lo es).
        if (error.response && error.response.status === 409 && error.response.data && error.response.data.message === SAVED_FILTER_LIMIT_MESSAGE) {
          this.$store.dispatch("toast/show", { message: `Alcanzó el máximo de ${this.maxPerLayer} filtros guardados para esta capa`, type: "warning" });
        } else if (error.response && error.response.status === 409) this.offerOverwriteByName(name, filter);
        else this.$store.dispatch("toast/show", { message: this.errorMessage(error), type: "error" });
      } finally {
        this.saving = false;
      }
    },
    // 409 de nombre duplicado (ver routes/saved_filter.go::CreateSavedFilter)
    // → se ofrece reemplazar el filtro existente con ese nombre en vez de
    // solo mostrar el error.
    offerOverwriteByName(name, filter) {
      const existing = this.presets.find(preset => preset.name === name);
      if (!existing) {
        this.$store.dispatch("toast/show", { message: "Ya existe un filtro guardado con ese nombre en esta capa", type: "error" });
        return;
      }
      this.$modal.show("dialog", {
        title: "Filtro existente",
        text: `Ya existe un filtro guardado llamado «${existing.name}» en esta capa. ¿Desea reemplazarlo con el filtro actual?`,
        buttons: [
          { title: "Cancelar" },
          {
            title: "Reemplazar",
            class: "vue-dialog-button saved-filters-confirm-button",
            handler: async () => {
              this.$modal.hide("dialog");
              await this.doOverwrite(existing, filter);
              this.name = "";
            },
          },
        ],
      });
    },
    // Cargar un filtro cruzado (advanced + crossLayer) puede tardar hasta un
    // minuto (consulta y procesa la geometría de la capa objetivo, ver
    // attributesTable.js::prepareCrossLayer) — se avisa antes de disparar la
    // carga, mismo criterio que el banner de progreso de AttributesTable.vue.
    // El resto de los filtros (rápidos o avanzados sin cruce) se cargan sin
    // preguntar, igual que cualquier otra acción de este modal.
    crossLayerTargetLabel(preset) {
      const advanced = preset.filter && preset.filter.mode === "advanced" && preset.filter.advanced;
      const crossLayer = advanced && advanced.crossLayer;
      return crossLayer && crossLayer.enabled ? (crossLayer.targetLayerLabel || "otra capa") : null;
    },
    async load(preset) {
      if (this.busy || !this.layer) return;
      const targetLabel = this.crossLayerTargetLabel(preset);
      if (targetLabel) {
        this.$modal.show("dialog", {
          title: "Filtro entre capas",
          text: `«${preset.name}» depende de la capa «${targetLabel}» y puede tardar hasta un minuto en aplicarse. ¿Desea continuar?`,
          buttons: [
            { title: "Cancelar" },
            {
              title: "Aplicar",
              class: "vue-dialog-button saved-filters-confirm-button",
              handler: async () => { this.$modal.hide("dialog"); await this.doLoad(preset); },
            },
          ],
        });
        return;
      }
      await this.doLoad(preset);
    },
    async doLoad(preset) {
      this.busy = true;
      try {
        await attributesTableService.applySavedFilterPreset(this.layer, preset);
        this.$store.dispatch("toast/show", { message: `Filtro «${preset.name}» aplicado`, type: "success" });
        // El zoom lo hace la tabla, que ya sabe traer el bbox de TODAS las
        // coincidencias y no solo de la página cargada (QA-175). Solo se avisa
        // cuando el preset se aplicó: si falló, el mapa sigue como estaba.
        this.$emit("applied", preset);
      } catch (error) {
        if (error.savedFilterIncompatible) {
          this.$store.commit("savedFilters/markIncompatible", { id: preset.id, reason: error.message });
          this.$store.dispatch("toast/show", { message: `«${preset.name}» ya no es compatible con el esquema actual de la capa y no se aplicó.`, type: "warning" });
        } else {
          this.$store.dispatch("toast/show", { message: error.message || "No se pudo aplicar el filtro guardado", type: "error" });
        }
      } finally {
        this.busy = false;
      }
    },
    async overwrite(preset) {
      if (!this.hasCurrentFilter || this.busy || !this.layer) return;
      const filter = attributesTableService.captureAppliedFilter(this.layer);
      if (!filter) return;
      await this.doOverwrite(preset, filter);
    },
    async doOverwrite(preset, filter) {
      this.busy = true;
      try {
        await this.$store.dispatch("savedFilters/overwrite", { layerId: this.layer.id, preset, filter });
        this.$store.dispatch("toast/show", { message: `Filtro «${preset.name}» actualizado`, type: "success" });
      } catch (error) {
        this.$store.dispatch("toast/show", { message: this.errorMessage(error), type: "error" });
      } finally {
        this.busy = false;
      }
    },
    startRename(preset) { this.editingId = preset.id; this.editName = preset.name; },
    cancelRename() { this.editingId = null; this.editName = ""; },
    async confirmRename(preset) {
      const name = this.editName.trim();
      if (!name || name === preset.name) { this.cancelRename(); return; }
      this.busy = true;
      try {
        await this.$store.dispatch("savedFilters/rename", { layerId: this.layer.id, preset, name });
        this.cancelRename();
      } catch (error) {
        this.$store.dispatch("toast/show", { message: this.errorMessage(error), type: "error" });
      } finally {
        this.busy = false;
      }
    },
    confirmDelete(preset) {
      this.$modal.show("dialog", {
        title: "Eliminar filtro guardado",
        text: `¿Eliminar el filtro guardado «${preset.name}»? El filtro aplicado ahora mismo en la capa no se ve afectado.`,
        buttons: [
          { title: "Cancelar" },
          {
            title: "Eliminar",
            class: "vue-dialog-button saved-filters-confirm-button danger",
            handler: async () => {
              this.$modal.hide("dialog");
              this.busy = true;
              try {
                await this.$store.dispatch("savedFilters/remove", { layerId: this.layer.id, id: preset.id });
              } catch (error) {
                this.$store.dispatch("toast/show", { message: this.errorMessage(error), type: "error" });
              } finally {
                this.busy = false;
              }
            },
          },
        ],
      });
    },
  },
};
</script>

<style scoped>
.saved-filters-modal { display:flex; flex-direction:column; max-height:80vh; overflow:hidden; padding:16px; background:#fff; }
.saved-filters-header { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:10px; color:var(--color-navy); }
.saved-filters-header button { display:flex; align-items:center; justify-content:center; width:28px; height:28px; border:0; border-radius:4px; background:transparent; color:#64748b; cursor:pointer; }
.save-current { display:flex; gap:8px; }
.save-current input { flex:1; min-width:0; box-sizing:border-box; padding:7px 10px; border:1px solid #cbd5e1; border-radius:5px; font-size:.8rem; }
.save-btn { flex-shrink:0; padding:7px 12px; border:1px solid var(--color-orange); border-radius:4px; background:var(--color-orange); color:#fff; font-size:.75rem; font-weight:600; cursor:pointer; white-space:nowrap; }
.save-btn:disabled, .save-current input:disabled { opacity:.5; cursor:not-allowed; }
.hint { margin:8px 0 0; color:#64748b; font-size:.72rem; }
.hint.warning { color:#b45309; font-weight:600; }
.preset-list { display:flex; flex-direction:column; gap:6px; max-height:50vh; margin:12px 0 0; padding:0; overflow:auto; list-style:none; }
.preset-row { display:flex; flex-direction:column; gap:6px; padding:8px 9px; border:1px solid #e2e8f0; border-radius:5px; background:#f8fafc; }
.preset-row.active { border-color:#22a060; background:#eafaf1; }
.preset-row.selected { border-color:var(--color-orange); background:#fff3e8; }
.preset-info { display:flex; align-items:center; gap:8px; min-width:0; cursor:pointer; }
.preset-edit { display:flex; flex-wrap:wrap; align-items:center; gap:6px; }
.preset-name { overflow:hidden; color:#334155; font-size:.8rem; font-weight:600; text-overflow:ellipsis; white-space:nowrap; }
.preset-badge { flex-shrink:0; padding:1px 7px; border-radius:10px; background:#22a060; color:#fff; font-size:.65rem; font-weight:700; }
.preset-badge.warning { background:#f59e0b; }
.preset-actions-bar { display:flex; flex-wrap:wrap; gap:6px; margin-top:12px; padding-top:10px; border-top:1px solid #e2e8f0; }
.text-btn { padding:4px 9px; border:1px solid #cbd5e1; border-radius:4px; background:#fff; color:var(--color-navy); font-size:.7rem; font-weight:600; cursor:pointer; }
.text-btn:disabled { opacity:.45; cursor:not-allowed; }
.text-btn.danger { border-color:#c53b38; color:#c53b38; }
.rename-input { flex:1; min-width:80px; box-sizing:border-box; padding:4px 7px; border:1px solid #cbd5e1; border-radius:4px; font-size:.75rem; }
</style>

<style>
/* Sin scope a propósito: el diálogo genérico de vue-js-modal (<v-dialog/>,
   App.vue) se monta fuera del árbol de este componente — mismo criterio que
   AttributesTable.vue::.attributes-table-confirm-button. */
.saved-filters-confirm-button {
  color: var(--color-navy, #0e2a40);
  font-weight: 700;
}
.saved-filters-confirm-button.danger {
  color: #c53b38;
}
</style>
